import { readConfig, hasRequiredIdentifier } from './lib/embedConfig';
import { renderEmbed } from './lib/renderEmbed';
import { setEmbedOrigin } from './lib/origin';

// Bootstrap for the no-iframe SEO embed. Scans for .famewall-embed
// containers, attaches an open Shadow DOM, injects the renderer CSS, and
// mounts the real Wall-Embeds React renderers inside. No iframe is ever
// created - this is the whole point of the SEO path.
//
// The mount/config logic lives in lib/renderEmbed.js + lib/embedConfig.js,
// shared with seo-embed/widget.jsx (the ESM mount()/custom-element entry for
// integrations) - this file only owns the page-level scan-once bootstrap.
const EMBED_SELECTOR = '.famewall-embed';
const DEFAULT_API_ORIGIN = 'https://embed.famewall.io';

function resolveScriptOrigin() {
  let script = document.currentScript;

  if (!script || !script.src) {
    const scripts = document.getElementsByTagName('script');
    for (let index = scripts.length - 1; index >= 0; index -= 1) {
      if (scripts[index].src && /newFrame\.js/i.test(scripts[index].src)) {
        script = scripts[index];
        break;
      }
    }
    if ((!script || !script.src) && scripts.length) {
      script = scripts[scripts.length - 1];
    }
  }

  try {
    return new URL(script.src).origin;
  } catch (err) {
    return DEFAULT_API_ORIGIN;
  }
}

// Captured while the deferred script is still executing synchronously, so
// document.currentScript is reliable.
const SCRIPT_ORIGIN = resolveScriptOrigin();

function ready(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
    return;
  }

  callback();
}

// Yields each per-embed mount onto its own task so the bootstrap does not
// hold the main thread for one big synchronous render (Lighthouse flagged
// 257ms + 243ms long tasks from newFrame.js). requestIdleCallback runs in
// the browser's idle period - the customer's hero paint, analytics, and
// any LCP-critical work win the priority race. Falls back to setTimeout(0)
// on Safari and any other browser without requestIdleCallback.
function scheduleEmbedMount(task) {
  const fallback = () => setTimeout(task, 0);

  if (typeof window === 'undefined') {
    return;
  }

  if (typeof window.requestIdleCallback === 'function') {
    try {
      window.requestIdleCallback(task, { timeout: 1500 });
      return;
    } catch (err) {
      // Some embedded WebViews ship a broken requestIdleCallback; fall through.
    }
  }

  fallback();
}

function mountEmbed(element) {
  if (element.getAttribute('data-loaded')) {
    return;
  }
  element.setAttribute('data-loaded', 'true');

  const config = readConfig(element);
  if (!hasRequiredIdentifier(config)) {
    return;
  }

  renderEmbed(element, config, SCRIPT_ORIGIN);
}

setEmbedOrigin(SCRIPT_ORIGIN);

ready(function bootstrap() {
  const embeds = document.querySelectorAll(EMBED_SELECTOR);
  Array.prototype.forEach.call(embeds, function each(element) {
    scheduleEmbedMount(function runMount() {
      try {
        mountEmbed(element);
      } catch (err) {
        // One broken embed must never take down the host page or sibling embeds.
        if (element.getAttribute('data-debug') === '1' && typeof console !== 'undefined') {
          console.error('Famewall Embed failed to mount', err);
        }
      }
    });
  });
});
